import { DateTime } from 'luxon';

const GRACE_PERIOD_DAYS = 7;

/**
 * Helper function to calculate days since the start of the month
 */
function getDaysSinceMonthStart(currentDate: DateTime): number {
  return currentDate.diff(currentDate.startOf('month'), 'days').days;
}

/**
 * Helper function to convert various date formats to DateTime
 */
function normalizeDate(date: DateTime | number | string): DateTime {
  if (typeof date === 'number') {
    return DateTime.fromMillis(date);
  }
  if (typeof date === 'string') {
    return DateTime.fromFormat(date, 'yyyy-MM-dd');
  }
  return date;
}

/**
 * Determines the minimum allowed date for status change requests based on business rules.
 *
 * Business Rule: Status change requests can't be requested for the previous month,
 * except when it's the first week of the following month.
 *
 * Note: This function uses the current date in the user's local timezone.
 * The business rule is based on calendar days, not business days.
 *
 * @param currentDate - The current date (defaults to DateTime.now())
 * @returns The minimum allowed date for status change requests
 */
export function getMinDateForStatusChangeRequest(
  currentDate: DateTime = DateTime.now()
): DateTime {
  const monthStart = currentDate.startOf('month');

  if (isInGracePeriodForStatusChangeRequest(currentDate)) {
    // If we're in the grace period, allow requests for the previous month
    return monthStart.minus({ month: 1 });
  } else {
    // Otherwise, only allow requests for the current month onwards
    return monthStart;
  }
}

/**
 * Determines if a status change request can be approved based on when it is supposed to take effect.
 * A request for a date that was only allowed during the grace period cannot be approved after the grace period has elapsed.
 *
 * @param requestEffectiveAt - When the status change is supposed to take effect (DateTime, millis, or 'yyyy-MM-dd' string)
 * @param currentDate - The current date (defaults to DateTime.now())
 * @returns True if the request can be approved, false otherwise
 */
export function canApproveStatusChangeRequest(
  requestEffectiveAt: DateTime | number | string,
  currentDate: DateTime = DateTime.now()
): boolean {
  const effectiveDate = normalizeDate(requestEffectiveAt);
  const minDate = getMinDateForStatusChangeRequest(currentDate);
  return effectiveDate >= minDate;
}

/**
 * Gets a human-readable description of the current date restrictions.
 *
 * @param currentDate - The current date (defaults to DateTime.now())
 * @returns A string describing the current restrictions
 */
export function getStatusChangeRequestDateRestrictionDescription(
  currentDate: DateTime = DateTime.now()
): string {
  if (isInGracePeriodForStatusChangeRequest(currentDate)) {
    const daysSinceMonthStart = getDaysSinceMonthStart(currentDate);
    const remainingDays = Math.ceil(GRACE_PERIOD_DAYS - daysSinceMonthStart);
    return `You can request status changes for the previous month until day ${GRACE_PERIOD_DAYS} of the current month. You have ${remainingDays} day${
      remainingDays !== 1 ? 's' : ''
    } remaining.`;
  } else {
    return 'You can only request status changes for the current month onwards.';
  }
}

/**
 * Checks if we're currently in the grace period for status change requests.
 *
 * @param currentDate - The current date (defaults to DateTime.now())
 * @returns True if we're in the grace period, false otherwise
 */
export function isInGracePeriodForStatusChangeRequest(
  currentDate: DateTime = DateTime.now()
): boolean {
  const daysSinceMonthStart = getDaysSinceMonthStart(currentDate);
  return daysSinceMonthStart < GRACE_PERIOD_DAYS;
}
